import * as React from 'react';
import { AudienceStatsFilterv1, AudienceStatsModelv1, NivoLineSeries } from '../models';
import Loading from 'modules/common/components/loading';
import EmptyDataMessage from './emptyDataMessage';
import DownloadIcon from './downloadIconSvg';
import InfoHover from 'modules/common/components/hovers/infoHover';
import { mapAudienceStatsToNivoLineSeries } from 'utils/analyticsHelpers';
import { NivoLineChart } from './charts/nivoLineChart';
import { ApiRequest } from 'api/network';
import { useEffect } from 'react';
import { useDefaultLocale } from 'modules/common/hooks/useDefaultLocale';

interface AnalyticsWidgetProps {
    id: string;
    lineChartTitle: string;
    reportFilter: Partial<AudienceStatsFilterv1>;
    showInfoHover?: boolean;
    infoHelperText?: string;
    truncateLabelAt?: number;
    downloadExcel: () => void;
    downloadImage: () => Promise<void>;
    fetchAudienceStats: (reportFilter: Partial<AudienceStatsFilterv1>) => ApiRequest<AudienceStatsModelv1[]>;
}

const AnalyticsWidget: React.FC<AnalyticsWidgetProps> = (props) => {
    const [audienceStats, setAudienceStats] = React.useState<NivoLineSeries[]>([]);
    const [isFetching, setIsFetching] = React.useState(true);
    const [itemsToDisplay, setItemsToDisplay] = React.useState(0);
    const widthToUse = 1500;
    useDefaultLocale();

    useEffect(() => {
        setIsFetching(true);
        const fetchData = async () => {
            try {
                const data = await props.fetchAudienceStats(props.reportFilter);
                const mappedData = mapAudienceStatsToNivoLineSeries(data);
                mappedData.sort((a, b) => b.id.localeCompare(a.id));
                setAudienceStats(mappedData);
                if (mappedData.length > 0) {
                    setItemsToDisplay(mappedData[0].data?.length || 0);
                }
            } catch (error) {
                setAudienceStats([]);
                setItemsToDisplay(0);
            }
        };

        fetchData();
        setIsFetching(false);
    }, [props.reportFilter]);

    return (
        <div className="analytics-widget" style={{ height: '100%' }}>
            <div className='downloadIcon'>  
                <DownloadIcon
                    downloadExcel={props.downloadExcel}
                    downloadImage={props.downloadImage}
                />
            </div>
            <div style={{ overflowX: "auto" }}>
                <div id={props.id} style={ itemsToDisplay > 4 ? {width: widthToUse, backgroundColor: '#fff'} : { backgroundColor: '#fff', minWidth: "400px"}}>
                    <div className="header">
                    <span style={{ whiteSpace: 'nowrap' }}>{props.lineChartTitle}</span>
                        {props.showInfoHover && (
                            <div style={{ flexShrink: 0, marginLeft: '4px' }}>
                                <InfoHover>{props.infoHelperText}</InfoHover>
                            </div>
                        )}
                    </div>
                    <div className="body">
                        {isFetching ? (
                            <div style={{ marginTop: 40 }}>
                                <Loading />
                            </div>
                        ) : (
                            <div>
                                {audienceStats ? (
                                    <NivoLineChart data={audienceStats} height={300} truncateLabelAt={props.truncateLabelAt} />
                                ) : (
                                    <div style={{ marginTop: 40 }}>
                                        <EmptyDataMessage />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsWidget;